import { useCallback, useEffect, useState } from 'react';
import { mount, unmount } from 'svelte';
import type { Component } from 'svelte';

/**
 * Create an instance of a svelte component which can be rendered from React
 *
 * This component will mount and unmount when its host component does. It will also re-mount when its props ref
 * changes. This gives you full control over when the component re-mounts:
 *  - use a new ref to "mount" the component on every render. This is like setting `innerHTML` every render.
 *  - memoize a set of props to only re-mount when props change. This is like setting `innerHTML` when the props change.
 *  - memoize a set of props where the props never really change (e.g. use signals for changing values). This ties the
 *    lifecycle of the component to the lifecycle of it's React host.
 *  - memoize a set of props where the memoization is keyed off a particular string state. This allows you to re-mount
 *    the svelte component on-demand when careful lifecycle management is needed (this is equivalent to React "key").
 */
export const useSvelteComponent = <T extends Record<string, any>>(component: Component<T>, props: T) => {
    const [svelteElement, setSvelteElement] = useState<HTMLDivElement | null>(null);
    const svelteRef = useCallback((node: HTMLDivElement | null) => {
        setSvelteElement(node);
    }, []);

    useEffect(() => {
        if (!svelteElement) return;

        const app = mount(component, {
            target: svelteElement,
            props,
        });

        return () => {
            unmount(app);
        };
    }, [component, props, svelteElement]);

    return svelteRef;
};
